import React, { useEffect, useState, useContext } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { BalanceContext } from "../context/BalanceContext";
import { ProjectsContext } from "../context/ProjectsContext";
import { motion } from "framer-motion";
import Layout from "../components/Layout";
import { FaChartLine, FaHistory as FaHistoryIcon } from "react-icons/fa";

const History = () => {
  const [balanceData, setBalanceData] = useState([]);
  const [profitLossData, setProfitLossData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedRangeLabel, setSelectedRangeLabel] = useState("");
  const [manualStartDate, setManualStartDate] = useState("");
  const [manualEndDate, setManualEndDate] = useState("");
  const [selectedProjects, setSelectedProjects] = useState(["All Projects"]);
  const [selectedRange, setSelectedRange] = useState("customRange");
  const { balancing } = useContext(BalanceContext);
  const { projects } = useContext(ProjectsContext);

  const COLORS = ["#10B981", "#EF4444"];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      const isPositive = value >= 0;
      const tooltipText = isPositive ? "Win" : "Loss";
      const tooltipColor = isPositive ? "#10B981" : "#EF4444";

      return (
        <div className="p-3 bg-gray-800 border border-gray-700 rounded-lg shadow-lg">
          <p className="text-sm" style={{ color: tooltipColor }}>
            {tooltipText}
          </p>
          <p className="text-sm text-gray-400">{`Date: ${label}`}</p>
          <p className="text-sm" style={{ color: tooltipColor }}>
            {`Value: ${value.toFixed(2)}€`}
          </p>
        </div>
      );
    }

    return null;
  };

  // Funcție pentru verificarea utilizatorului VisionRoyale
  const isVisionRoyale = () => {
    const token = localStorage.getItem("token");
    if (!token) return false;
    try {
      const payload = token.split(".")[1];
      const decoded = JSON.parse(atob(payload));
      return decoded.username === "VisionRoyale";
    } catch (error) {
      console.error("Error decoding token:", error);
      return false;
    }
  };

  useEffect(() => {
    // Nu procesa datele dacă este VisionRoyale
    if (isVisionRoyale()) return;

    const storedBets = JSON.parse(localStorage.getItem("betsByDate")) || {};
    processBetsData(storedBets);
  }, []);

  useEffect(() => {
    // Nu procesa datele dacă este VisionRoyale
    if (isVisionRoyale()) return;

    const storedBets = JSON.parse(localStorage.getItem("betsByDate")) || {};
    processBetsData(storedBets, startDate, endDate);
  }, [selectedProjects, startDate, endDate]);

  const processBetsData = (betsByDate, start = null, end = null) => {
    if (!betsByDate || typeof betsByDate !== "object") {
      console.error("Invalid events data");
      return;
    }

    const balanceHistory = [];
    const profitLossHistory = [];
    const winLossCount = { won: 0, lost: 0 };
    const transactions = [];

    let currentBalance = balancing || 0;

    Object.keys(betsByDate).forEach((date) => {
      const betDate = new Date(date);
      if ((!start || betDate >= start) && (!end || betDate <= end)) {
        const bets = betsByDate[date];
        let dailyProfit = 0;
        let dailyLoss = 0;

        bets.forEach((bet) => {
          if (
            selectedProjects.includes("All Projects") ||
            selectedProjects.includes(bet.title)
          ) {
            if (bet.isReady) {
              if (bet.updatedStatus === "WON") {
                dailyProfit += bet.winnings - bet.stake;
                winLossCount.won += 1;
              } else if (bet.updatedStatus === "LOST") {
                dailyLoss += bet.stake;
                winLossCount.lost += 1;
              }

              transactions.push({
                id: bet.id,
                date,
                type: bet.updatedStatus === "WON" ? "Event Won" : "Event Lost",
                amount:
                  bet.updatedStatus === "WON"
                    ? `+${(bet.winnings - bet.stake).toFixed(2)}€`
                    : `-${bet.stake.toFixed(2)}€`,
                details: bet.title,
              });
            }
          }
        });

        currentBalance += dailyProfit - dailyLoss;
        balanceHistory.push({ date, balance: currentBalance });

        const netProfitLoss = dailyProfit - dailyLoss;
        profitLossHistory.push({ date, netProfitLoss });
      }
    });

    setBalanceData(balanceHistory);
    setProfitLossData(profitLossHistory);
    setPieData([
      { name: "Won", value: winLossCount.won },
      { name: "Lost", value: winLossCount.lost },
    ]);
    setTransactionHistory(transactions);
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);

    if (start && end) {
      setSelectedRangeLabel(
        `Selected Range: ${start.toLocaleDateString()} - ${end.toLocaleDateString()}`,
      );
      setSelectedRange("customRange");

      setManualStartDate(start.toISOString().split("T")[0]);
      setManualEndDate(end.toISOString().split("T")[0]);
    } else {
      setSelectedRangeLabel("");
      setSelectedRange("");
      setManualStartDate("");
      setManualEndDate("");
    }
  };

  const handleProjectSelect = (e) => {
    const selectedOption = e.target.value;
    setSelectedProjects([selectedOption]);
  };

  const handleQuickRange = (range) => {
    const today = new Date();
    let start, end;

    switch (range) {
      case "today":
        start = new Date(today);
        end = new Date(today);
        break;
      case "thisWeek":
        start = new Date(today.setDate(today.getDate() - today.getDay()));
        end = new Date(today.setDate(today.getDate() + 6));
        break;
      case "thisMonth":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case "thisYear":
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today.getFullYear(), 11, 31);
        break;
      case "lastWeek":
        start = new Date(today.setDate(today.getDate() - today.getDay() - 7));
        end = new Date(today.setDate(today.getDate() + 6));
        break;
      case "lastMonth":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case "lastYear":
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear() - 1, 11, 31);
        break;
      case "customRange":
        start = null;
        end = null;
        break;
      default:
        start = null;
        end = null;
    }

    setStartDate(start);
    setEndDate(end);
    setSelectedRange(range);

    if (start && end) {
      setSelectedRangeLabel(
        `Selected Range: ${start.toLocaleDateString()} - ${end.toLocaleDateString()}`,
      );
    } else {
      setSelectedRangeLabel("");
    }

    if (range !== "customRange") {
      setManualStartDate("");
      setManualEndDate("");
    }
  };

  const handleManualRangeSubmit = () => {
    const start = new Date(manualStartDate);
    const end = new Date(manualEndDate);

    if (start && end && start <= end) {
      setStartDate(start);
      setEndDate(end);

      setSelectedRangeLabel(
        `Selected Range: ${start.toLocaleDateString()} - ${end.toLocaleDateString()}`,
      );
    } else {
      alert("Date invalide! Begin Date must be lower than End Date.");
    }
  };

  const sortedBalanceData = [...balanceData].sort(
    (a, b) => new Date(a.date) - new Date(b.date),
  );
  const sortedProfitLossData = [...profitLossData].sort(
    (a, b) => new Date(a.date) - new Date(b.date),
  );

  const totalProfit =
    balanceData.length > 0 ? balanceData[balanceData.length - 1].balance : 0;
  const winRate =
    pieData.length > 0 && pieData[0].value + pieData[1].value > 0
      ? (pieData[0].value / (pieData[0].value + pieData[1].value)) * 100
      : 0;

  // BLOCARE PENTRU VisionRoyale - return early cu design îmbunătățit
  if (isVisionRoyale()) {
    return (
      <Layout>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-4"
        >
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl font-bold mb-6 text-green-400 text-center">
              History Dashboard
            </h1>
            <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg p-4 mb-4 text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center mx-auto mb-6">
                <FaHistoryIcon className="text-white text-3xl" />
              </div>
              <p className="text-yellow-400 font-medium">
                ⚠️ History is disabled for Test User VisionRoyale.
              </p>
            </div>
          </div>
        </motion.div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-4"
      >
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold mb-6 text-green-400 text-center">
            History Dashboard
          </h1>

          {/* Calendar și controale - ROW 1 */}
          <div className="mb-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Coloana 1: Calendar */}
              <div className="bg-gray-800/50 border border-gray-700/50 p-5 rounded-xl shadow-lg w-full">
                <h2 className="text-lg font-semibold mb-4 text-green-400 text-center">
                  Select Time from Calendar
                </h2>
                <div className="flex justify-center mt-12 px-4">
                  <div className="mt-3 scale-125 transform origin-center lg:scale-135">
                    <DatePicker
                      selected={startDate}
                      onChange={handleDateChange}
                      startDate={startDate}
                      endDate={endDate}
                      selectsRange
                      inline
                      className="bg-gray-800 text-white custom-datepicker"
                      dayClassName={() =>
                        "hover:bg-green-600 transition-colors duration-200"
                      }
                    />
                  </div>
                </div>
                {selectedRangeLabel && (
                  <div className="mt-4 text-center">
                    <p className="text-green-400 text-sm font-medium">
                      {selectedRangeLabel}
                    </p>
                  </div>
                )}
              </div>

              {/* Coloana 2: Select Date Range */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-gray-800/50 border border-gray-700/50 p-5 rounded-xl shadow-lg w-full"
              >
                <h2 className="text-lg font-semibold text-green-400 text-center mb-4">
                  Select Time Range
                </h2>
                <select
                  onChange={(e) => handleQuickRange(e.target.value)}
                  className="w-full p-3 border-2 border-green-500 rounded-lg bg-gray-700 text-white text-center hover:bg-gray-600 transition duration-300 mb-4"
                  value={selectedRange}
                >
                  <option value="customRange">Custom Range</option>
                  <option value="today">Today</option>
                  <option value="thisWeek">This Week</option>
                  <option value="thisMonth">This Month</option>
                  <option value="thisYear">This Year</option>
                  <option value="lastWeek">Last Week</option>
                  <option value="lastMonth">Last Month</option>
                  <option value="lastYear">Last Year</option>
                  <option value="">All Time</option>
                </select>
                {selectedRange === "customRange" && (
                  <div className="flex flex-col space-y-3">
                    <div className="flex flex-col">
                      <label className="text-gray-300 mb-1 text-center">
                        Begin Date
                      </label>
                      <input
                        type="date"
                        value={manualStartDate}
                        onChange={(e) => setManualStartDate(e.target.value)}
                        className="bg-gray-700 text-white p-2 rounded-lg text-center border border-gray-600"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-gray-300 mb-1 text-center">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={manualEndDate}
                        onChange={(e) => setManualEndDate(e.target.value)}
                        className="bg-gray-700 text-white p-2 rounded-lg text-center border border-gray-600"
                      />
                    </div>
                    <button
                      onClick={handleManualRangeSubmit}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition duration-300 font-medium mt-2"
                    >
                      Apply Custom Range
                    </button>
                  </div>
                )}
              </motion.div>

              {/* Coloana 3: Statistici */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="bg-gray-800/50 border border-gray-700/50 p-5 rounded-xl shadow-lg w-full"
              >
                <h2 className="text-lg font-semibold text-green-400 text-center mb-4">
                  Select Project
                </h2>
                <select
                  value={selectedProjects[0]}
                  onChange={handleProjectSelect}
                  className="w-full p-3 border-2 border-green-500 rounded-lg bg-gray-700 text-white text-center hover:bg-gray-600 transition duration-300 mb-6"
                >
                  <option value="All Projects">All Projects</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.name}>
                      {project.name}
                    </option>
                  ))}
                </select>
                <h2 className="text-lg font-semibold text-green-400 text-center mb-4">
                  Statistics
                </h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition duration-300">
                    <p className="text-gray-300">
                      {totalProfit >= 0 ? "Total Profit:" : "Total Loss:"}
                    </p>
                    <p
                      className={`text-xl font-bold ${
                        totalProfit >= 0 ? "text-green-400" : "text-red-400"
                      }`}
                    >
                      {totalProfit.toFixed(2)}€
                    </p>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition duration-300">
                    <p className="text-gray-300">
                      {winRate >= 0 ? "Win Rate:" : "Lost Rate:"}
                    </p>
                    <p
                      className={`text-xl font-bold ${
                        winRate >= 0 ? "text-green-400" : "text-red-400"
                      }`}
                    >
                      {winRate.toFixed(2)}%
                    </p>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition duration-300">
                    <p className="text-gray-300">Total Events:</p>
                    <p className="text-green-400 text-xl font-bold">
                      {pieData.length > 0
                        ? pieData[0].value + pieData[1].value
                        : 0}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Grafice - ROW 2 */}
          <div className="mb-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Grafic balanță */}
              <div className="bg-gray-800/50 border border-gray-700/50 p-5 rounded-xl shadow-lg w-full h-full">
                <h2 className="text-lg font-semibold mb-4 text-center text-green-400">
                  Balance Evolution
                </h2>
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={sortedBalanceData}
                      margin={{ top: 20, bottom: 30 }}
                    >
                      <XAxis
                        dataKey="date"
                        stroke="#9CA3AF"
                        tick={{ fill: "#9CA3AF", fontSize: 11 }}
                        tickFormatter={(date) =>
                          new Date(date).toLocaleDateString()
                        }
                        angle={-45}
                        textAnchor="end"
                      />
                      <YAxis stroke="#9CA3AF" tick={{ fill: "#9CA3AF" }} />
                      <CartesianGrid stroke="#4B5563" strokeDasharray="3 3" />
                      <Tooltip content={<CustomTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="balance"
                        stroke="#10B981"
                        strokeWidth={3}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Grafic profit/pierdere */}
              <div className="bg-gray-800/50 border border-gray-700/50 p-5 rounded-xl shadow-lg w-full h-full">
                <h2 className="text-lg font-semibold mb-4 text-center text-green-400">
                  Profit & Loss
                </h2>
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={sortedProfitLossData}
                      margin={{ top: 20, bottom: 30 }}
                    >
                      <XAxis
                        dataKey="date"
                        stroke="#9CA3AF"
                        tick={{ fill: "#9CA3AF", fontSize: 11 }}
                        tickFormatter={(date) =>
                          new Date(date).toLocaleDateString()
                        }
                        angle={-45}
                        textAnchor="end"
                      />
                      <YAxis stroke="#9CA3AF" tick={{ fill: "#9CA3AF" }} />
                      <CartesianGrid stroke="#4B5563" strokeDasharray="3 3" />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar
                        dataKey="netProfitLoss"
                        barSize={25}
                        radius={[3, 3, 0, 0]}
                      >
                        {sortedProfitLossData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              entry.netProfitLoss >= 0 ? "#10B981" : "#EF4444"
                            }
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Grafic Win/Loss Ratio */}
              <div className="bg-gray-800/50 border border-gray-700/50 p-5 rounded-xl shadow-lg w-full h-full">
                <h2 className="text-lg font-semibold mb-4 text-center text-green-400">
                  Win/Loss Ratio
                </h2>
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={70}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100).toFixed(0)}%`
                        }
                        labelLine={false}
                      >
                        {pieData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [`${value} events`, "Count"]}
                        contentStyle={{
                          backgroundColor: "#1F2937",
                          border: "1px solid #374151",
                          borderRadius: "8px",
                          color: "#FFFFFF",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          {/* Istoric tranzacții - ROW 3 */}
          <div className="bg-gray-800/50 border border-gray-700/50 p-6 rounded-xl shadow-lg">
            <h2 className="text-xl font-semibold mb-6 text-center text-green-400">
              Transaction History
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-gray-900 rounded-lg">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="px-4 py-3 text-left text-gray-300 font-medium">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-gray-300 font-medium">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-gray-300 font-medium">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-gray-300 font-medium">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {transactionHistory.map((transaction) => (
                    <tr
                      key={transaction.id}
                      className="border-b border-gray-700 hover:bg-gray-700 transition duration-200"
                    >
                      <td className="px-4 py-3 text-gray-300">
                        {transaction.date}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            transaction.type === "Event Won"
                              ? "bg-green-900/30 text-green-400"
                              : "bg-red-900/30 text-red-400"
                          }`}
                        >
                          {transaction.type}
                        </span>
                      </td>
                      <td
                        className={`px-4 py-3 font-bold ${
                          transaction.amount.startsWith("+")
                            ? "text-green-400"
                            : "text-red-400"
                        }`}
                      >
                        {transaction.amount}
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        {transaction.details}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {transactionHistory.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No transactions found for the selected period
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </Layout>
  );
};

export default History;
